import {push} from "connected-react-router";
import {change, reset, setSubmitSucceeded, startSubmit, stopSubmit,} from "redux-form";
import {call, fork, put, select, take} from "redux-saga/effects";
import {createPastCaseProjectAPI} from "src/infra/api/service/past_case_project_api";
import {NotificationCustomer} from "../../config/view_const/notification";
import {AppError, ErrorType} from "../../domain/model/app_error";
import {AppNotificationType} from "../../domain/model/app_notification_message";
import {Customer} from "../../domain/model/customer";
import {JobCareer} from "../../domain/model/job_career";
import {PastCaseProject} from "../../domain/model/past_case_project";
import {User} from "../../domain/model/user";
import {ICustomerRepository, TUpdateCustomerParams,} from "../../domain/repository/customer_repository";
import {
    IJobCareerRepository,
    TCreateJobCareerParams,
    TUpdateJobCareerParams,
} from "../../domain/repository/job_career_repository";
import {
    IPastCaseProjectRepository,
    TCreatePastCaseProjectParams,
    TUpdatePastCaseProjectParams,
} from "../../domain/repository/past_case_project_repository";
import {IUserRepository} from "../../domain/repository/user_repository";
import {createAuthUseCase, IAuthUseCase,} from "../../domain/usecase/auth_usecase";
import {createFileUsecase, IFileUseCase,} from "../../domain/usecase/file_usecase";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {createCustomerAPI} from "../../infra/api/service/customer_api";
import {createJobCareerAPI} from "../../infra/api/service/job_career_api";
import {createUserAPI} from "../../infra/api/service/user_api";
import {
    createAppNotificationMessageActionCreator,
    IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {createAuthActionCreator, IAuthActionCreator,} from "../action/auth_action";
import {
    createCustomerMypageActionCreator,
    CustomerMypageActionType,
    ICustomerMypageActionCreator,
    IRequestUpdateCustomerAction,
    IRequestUpdateEmailAction,
    IRequestUpdatePasswordAction,
} from "../action/customer_mypage_action";
import {AddAppNotificationRequest} from "../request/app_notification_message_request";
import {
    DestroyCustomerPDFByCustomerResponse,
    GenerateCustomerPDFByCustomerResponse,
    GetMeCustomerByCustomerResponse,
    UpdateCustomerByCustomerResponse,
    UpdateEmailByCustomerResponse,
    UpdatePasswordByCustomerResponse,
} from "../response/mypage_response";
import {AppState} from "../store/app_state";
import {AuthState} from "../store/auth_state";
import {CustomerMypageState} from "../store/customer_mypage_state";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const userRepository: IUserRepository = createUserAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const jobCareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const pastCaseProjectRepository: IPastCaseProjectRepository =
    createPastCaseProjectAPI(apiClient);
const authUseCase: IAuthUseCase = createAuthUseCase(
    userRepository,
    customerRepository
);
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: ICustomerMypageActionCreator =
    createCustomerMypageActionCreator();
const actionCreatorForAuth: IAuthActionCreator = createAuthActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
    createAppNotificationMessageActionCreator();

function* handleGetMeCustomerInCustomerMypage() {
    while (true) {
        try {
            yield take(CustomerMypageActionType.REQUEST_GET_ME_CUSTOMER);
            const user: User = yield call(getMeCustomer);
            if (user.customer) {
                const customer: Customer = user.customer;
                const email: string = user.email;
                customer.email = email;

                const jobCareers: JobCareer[] = yield call(getAllByCustomerJobCareer);
                const pastCaseProjects: PastCaseProject[] = yield call(
                    getAllByCustomerPastCaseProject
                );

                const res: GetMeCustomerByCustomerResponse =
                    new GetMeCustomerByCustomerResponse();
                res.customer = customer;
                res.jobCareers = jobCareers;
                res.pastCaseProjects = pastCaseProjects;
                yield put(actionCreator.callbackGetMeCustomerAction(true, res));
            } else {
                throw new AppError(
                    "UNKNOWN CODE",
                    ErrorType.CLIENT_ERROR,
                    "異常が発生しました"
                );
            }
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGetMeCustomerAction(false));
        }
    }
}

function* handleUpdateCustomerInCustomerMypage() {
    while (true) {
        const formName: string = "CustomerMypageMainCustomerForm";
        try {
            const action: IRequestUpdateCustomerAction = yield take(
                CustomerMypageActionType.REQUEST_UPDATE_CUSTOMER
            );
            yield put(startSubmit(formName));

            // Customer Update
            let iconURL: string;
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            if (action.item.uploadFileIconURL) {
                iconURL = yield call(
                    createCustomerIcon,
                    user,
                    action.item.uploadFileIconURL
                );
            } else if (/.*\/static\/public\/.*/.test(action.item.iconURL)) {
                // static/public/ が含まれているならdefault 画像
                iconURL = "";
            } else {
                iconURL = action.item.iconURL;
            }
            const updateParams: TUpdateCustomerParams = {
                name: action.item.name,
                nameKana: action.item.nameKana,
                iconURL,
                birthdate: action.item.birthdate,
                gender: action.item.gender,
                phoneNumber: action.item.phoneNumber,
                pr: action.item.pr,
                address: action.item.address,
                academicBackgrounds: action.item.academicBackgrounds,
                requirements: action.item.requirements,
                languages: action.item.languages,
                moreLanguage: action.item.moreLanguage,
                speakingLevel: action.item.speakingLevel,
                resumeURL: action.item.resumeURL,
                jobHistoryURL: action.item.jobHistoryURL,
                workingFrequency: action.item.workingFrequency,
                requestedFee: action.item.requestedFee,
                requestedCondition: action.item.requestedCondition,
                dutySummary: action.item.dutySummary,
                workIntention: action.item.workIntention,
                fullTimePossibility: action.item.fullTimePossibility,
                brokerageProjectPossibility: action.item.brokerageProjectPossibility,
                portfolio: action.item.portfolio,
                birthplace: action.item.birthplace,
                facebookURL: action.item.facebookURL,
                linkedInURL: action.item.linkedInURL,
                xURL: action.item.xURL,
            };
            const customer: Customer = yield call(updateCustomer, updateParams);

            // jobCareers Update
            const jobCareers: JobCareer[] = [];

            const customerSelector = (state: AppState) => state.customerMypageReducer;
            const _customerMypageState: CustomerMypageState = yield select(
                customerSelector
            );
            const _jobCareers: JobCareer[] = _customerMypageState.jobCareers
                ? _customerMypageState.jobCareers
                : [];

            // 削除対象: 現在の状態にIDが存在しており、新規状態に無い
            let jobCareerForDelete: JobCareer[] = [];
            if (action.item.jobCareers) {
                jobCareerForDelete = _jobCareers.filter((item: JobCareer) => {
                    const target = action.item.jobCareers.filter((_item) => {
                        return item.id === _item.id;
                    });
                    return target.length === 0;
                });
            }

            if (action.item.jobCareers) {
                for (const item of action.item.jobCareers) {
                    if (!item.id || item.id === "") {
                        jobCareers.push(yield call(createJobCareer, item));
                    } else {
                        jobCareers.push(yield call(updateJobCareer, item));
                    }
                }
            }

            for (const item of jobCareerForDelete) {
                yield call(deleteJobCareer, item.id);
            }

            // PastCaseProject Update
            const pastCaseProjects: PastCaseProject[] = [];
            if (action.item.pastCaseProjects) {
                for (const item of action.item.pastCaseProjects) {
                    if (!item) {
                        continue;
                    }
                    if (!item.id || item.id === "") {
                        pastCaseProjects.push(yield call(createPastCaseProject, item));
                    } else {
                        pastCaseProjects.push(yield call(updatePastCaseProject, item));
                    }
                }
            }

            if (action.item.multiSkillTagIds) {
                yield call(
                    updateMultiSkillTag,
                    action.item.multiSkillTagIds.filter((item) => item)
                );
            }

            const me: User = yield call(getMeCustomer);

            const res: UpdateCustomerByCustomerResponse =
                new UpdateCustomerByCustomerResponse();
            res.customer = me.customer!;
            res.jobCareers = jobCareers;
            res.pastCaseProjects = pastCaseProjects;
            yield put(actionCreator.callbackUpdateCustomerAction(true, res));
            yield put(change(formName, "iconURL", iconURL));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(push(`/customer/mypage/customer`));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationCustomer.successUpdateCustomer;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdateCustomerAction(false));
        }
    }
}

function* handleUpdatePasswordInCustomerMypage() {
    while (true) {
        const formName: string = "CustomerMypageMainPasswordForm";
        try {
            const action: IRequestUpdatePasswordAction = yield take(
                CustomerMypageActionType.REQUEST_UPDATE_PASSWORD
            );
            yield put(startSubmit(formName));
            yield call(
                updatePassword,
                action.item.currentPassword,
                action.item.newPassword
            );
            const updatePasswordRes: UpdatePasswordByCustomerResponse =
                new UpdatePasswordByCustomerResponse();
            yield put(
                actionCreator.callbackUpdatePasswordAction(true, updatePasswordRes)
            );
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(reset(formName));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationCustomer.successUpdatePassword;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdatePasswordAction(false));
        }
    }
}

function* handleUpdateEmailInCustomerMypage() {
    while (true) {
        const formName: string = "CustomerMypageMainEmailForm";
        try {
            const action: IRequestUpdateEmailAction = yield take(
                CustomerMypageActionType.REQUEST_UPDATE_EMAIL
            );
            yield put(startSubmit(formName));
            yield call(updateEmail, action.item.currentPassword, action.item.email);
            const updateEmailRes: UpdateEmailByCustomerResponse =
                new UpdateEmailByCustomerResponse();
            yield put(actionCreator.callbackUpdateEmailAction(true, updateEmailRes));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(actionCreatorForAuth.requestGetMeAction());
            yield put(reset(formName));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationCustomer.successUpdateEmail;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdateEmailAction(false));
        }
    }
}

function* handleGenerateCustomerPDFInCustomerMypage() {
    while (true) {
        try {
            yield take(CustomerMypageActionType.REQUEST_GENERATE_CUSTOMER_PDF);

            const base64 = yield call(generateCustomerPDF);
            // https://gist.github.com/meetzaveri/952b5482ea310f90036ac28728df4c73
            const base64str = `data:application/pdf;base64,${base64}`;
            const arr = base64str.split(",");
            // decode base64 string, remove space for IE compatibility
            const binary = atob(arr[1]);
            const len = binary.length;
            const buffer = new ArrayBuffer(len);
            const view = new Uint8Array(buffer);
            for (let i = 0; i < len; i++) {
                view[i] = binary.charCodeAt(i);
            }
            // create the blob object with content-type "application/pdf"
            const blob1 = new Blob([view], {type: "application/pdf"});

            const blobURL = window.URL.createObjectURL(blob1);
            const res: GenerateCustomerPDFByCustomerResponse =
                new GenerateCustomerPDFByCustomerResponse();
            res.blobURL = blobURL;
            yield put(actionCreator.callbackGenerateCustomerPDFAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGenerateCustomerPDFAction(false));
        }
    }
}

function* handleDestroyCustomerPDFInCustomerMypage() {
    while (true) {
        try {
            yield take(CustomerMypageActionType.REQUEST_DESTROY_CUSTOMER_PDF);
            const customerMypageSelector = (state: AppState) =>
                state.customerMypageReducer;
            const _customerMyPageState: CustomerMypageState = yield select(
                customerMypageSelector
            );
            const customerBlobURL = _customerMyPageState.blobURL;

            // blobURLの解放
            if (customerBlobURL) {
                window.URL.revokeObjectURL(customerBlobURL);
            }

            const res: DestroyCustomerPDFByCustomerResponse =
                new DestroyCustomerPDFByCustomerResponse();

            yield put(actionCreator.callbackDestroyCustomerPDFAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackDestroyCustomerPDFAction(false));
        }
    }
}

const getMeCustomer = () => {
    return userRepository.getMe();
};

const updateCustomer = (updateCustomerParams: TUpdateCustomerParams) => {
    return customerRepository.update(updateCustomerParams);
};

const updateMultiSkillTag = (ids: string[]) => {
    return customerRepository.updateMultiSkillTag(ids);
};

const updatePassword = (currentPassword: string, newPassword: string) => {
    return authUseCase.updatePassword(currentPassword, newPassword);
};

const updateEmail = (currentPassword: string, email: string) => {
    return authUseCase.updateEmail(currentPassword, email);
};

const createCustomerIcon = (user: User, uploadFile: File) => {
    return fileUseCase.createCustomerIcon(user, uploadFile);
};

const getAllByCustomerJobCareer = (): Promise<JobCareer[]> => {
    return jobCareerRepository.getAllByCustomer();
};

const createJobCareer = (
    createJobCareerRequest: TCreateJobCareerParams
): Promise<JobCareer> => {
    return jobCareerRepository.create(createJobCareerRequest);
};

const updateJobCareer = (
    updateJobCareerRequest: TUpdateJobCareerParams
): Promise<JobCareer> => {
    return jobCareerRepository.update(updateJobCareerRequest);
};

const deleteJobCareer = (jobCareerId: string): Promise<void> => {
    return jobCareerRepository.delete(jobCareerId);
};

const getAllByCustomerPastCaseProject = (): Promise<PastCaseProject[]> => {
    return pastCaseProjectRepository.getAllByCustomer();
};

const createPastCaseProject = (
    createPastCaseProjectRequest: TCreatePastCaseProjectParams
): Promise<PastCaseProject> => {
    return pastCaseProjectRepository.create(createPastCaseProjectRequest);
};

const updatePastCaseProject = (
    updatePastCaseProjectRequest: TUpdatePastCaseProjectParams
): Promise<PastCaseProject> => {
    return pastCaseProjectRepository.update(updatePastCaseProjectRequest);
};

const generateCustomerPDF = (): Promise<string> => {
    return customerRepository.generatePDF();
};

export {
    handleDestroyCustomerPDFInCustomerMypage,
    handleGenerateCustomerPDFInCustomerMypage,
    handleGetMeCustomerInCustomerMypage,
    handleUpdateCustomerInCustomerMypage,
    handleUpdateEmailInCustomerMypage,
    handleUpdatePasswordInCustomerMypage,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_mypage_handler.ts